// Photo mode's screen parts (decision 0060; the game owns photo mode's state, its camera and its look). The camera
// surface (`PhotoSurface`) covers the screen under a docked panel (`Screen` with `variant: "dock"`) and turns every
// input into three calls the game answers: `move` (the pivot: forward, right, up, each −1..1, over `dt` seconds),
// `orbit` (radians: > 0 turns the view right, raises the camera) and `zoom` (a factor: > 1 zooms in).
//   - keyboard: W A S D move, Q and E down and up, + and − zoom (held keys move every frame);
//   - mouse: a drag orbits, the wheel zooms;
//   - touch: one finger orbits, two fingers pinch to zoom and move the pivot as they slide;
//   - pad (the standard mapping): the left stick moves, the right stick orbits, the shoulders go down and up, the
//     triggers zoom; the surface marks itself `data-pad-stick="off"`, so the UI's navigation leaves the left stick to
//     it (the d-pad still moves focus in the panel).
// `createPhotoInput` is the pure part (events in, calls out), tested without a browser.
import { h, type VNode } from "preact";
import { useEffect, useRef } from "preact/hooks";
import type { PadLike } from "./pad";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type El = VNode<any>;

/** What photo mode's input calls. */
export interface PhotoHandlers {
  move(forward: number, right: number, up: number, dt: number): void;
  orbit(dYaw: number, dPitch: number): void;
  zoom(factor: number): void;
}

/** The input's rates. */
export interface PhotoInputOptions {
  /** Orbit per dragged pixel (rad; default 0.006). */
  orbitPerPx?: number;
  /** Orbit at full right-stick (rad/s; default 2). */
  padOrbit?: number;
  /** Zoom per wheel notch or + / − press (default 1.12), and per second of a held trigger (default 1.8). */
  zoomStep?: number;
  zoomRate?: number;
  /** Pivot movement per pixel of a two-finger slide, as stick input per second (default 0.02). */
  slidePerPx?: number;
  /** Stick dead zone (default 0.2). */
  dead?: number;
}

/** The pure input: feed events and frames; it calls the handlers. */
export interface PhotoInput {
  key(code: string, down: boolean): boolean;
  pointerDown(id: number, x: number, y: number): void;
  pointerMove(id: number, x: number, y: number): void;
  pointerUp(id: number): void;
  wheel(deltaY: number): void;
  /** Once per frame: held keys and the pads move, over `dt` seconds. */
  frame(dt: number, pads?: readonly (PadLike & { buttons: readonly { pressed: boolean; value?: number }[] } | null)[]): void;
  /** The keys held now. */
  held(): string[];
}

const MOVE_KEYS: Record<string, [number, number, number]> = { KeyW: [1, 0, 0], KeyS: [-1, 0, 0], KeyD: [0, 1, 0], KeyA: [0, -1, 0], KeyE: [0, 0, 1], KeyQ: [0, 0, -1] };
const ZOOM_KEYS: Record<string, number> = { Equal: 1, NumpadAdd: 1, Minus: -1, NumpadSubtract: -1 };

/** Make the pure input over the game's handlers. */
export function createPhotoInput(hd: PhotoHandlers, o: PhotoInputOptions = {}): PhotoInput {
  const orbitPx = o.orbitPerPx ?? 0.006;
  const padOrbit = o.padOrbit ?? 2;
  const zoomStep = o.zoomStep ?? 1.12;
  const zoomRate = o.zoomRate ?? 1.8;
  const slide = o.slidePerPx ?? 0.02;
  const dead = o.dead ?? 0.2;
  const held = new Set<string>();
  const pointers = new Map<number, { x: number; y: number }>();
  let pinch = 0;
  let centre: { x: number; y: number } | null = null;
  const two = (): { d: number; c: { x: number; y: number } } => {
    const [a, b] = [...pointers.values()];
    return { d: Math.hypot(a!.x - b!.x, a!.y - b!.y), c: { x: (a!.x + b!.x) / 2, y: (a!.y + b!.y) / 2 } };
  };
  const axis = (v: number | undefined): number => (v === undefined || Math.abs(v) < dead ? 0 : v);
  return {
    key(code, down) {
      if (MOVE_KEYS[code]) {
        if (down) held.add(code);
        else held.delete(code);
        return true;
      }
      if (ZOOM_KEYS[code] !== undefined) {
        if (down) hd.zoom(ZOOM_KEYS[code]! > 0 ? zoomStep : 1 / zoomStep);
        return true;
      }
      return false;
    },
    pointerDown(id, x, y) {
      pointers.set(id, { x, y });
      if (pointers.size === 2) {
        const t = two();
        pinch = t.d;
        centre = t.c;
      }
    },
    pointerMove(id, x, y) {
      const was = pointers.get(id);
      if (!was) return;
      pointers.set(id, { x, y });
      if (pointers.size === 1) hd.orbit((x - was.x) * orbitPx, (y - was.y) * orbitPx);
      else if (pointers.size === 2) {
        const t = two();
        if (pinch > 0 && t.d > 0) hd.zoom(t.d / pinch);
        if (centre) hd.move(-(t.c.y - centre.y) * slide, -(t.c.x - centre.x) * slide, 0, 1);
        pinch = t.d;
        centre = t.c;
      }
    },
    pointerUp(id) {
      pointers.delete(id);
      pinch = 0;
      centre = null;
    },
    wheel(dy) {
      if (dy !== 0) hd.zoom(dy < 0 ? zoomStep : 1 / zoomStep);
    },
    frame(dt, pads = []) {
      let f = 0;
      let r = 0;
      let u = 0;
      for (const k of held) {
        const m = MOVE_KEYS[k]!;
        f += m[0];
        r += m[1];
        u += m[2];
      }
      let oy = 0;
      let op = 0;
      let z = 0;
      for (const p of pads) {
        if (!p) continue;
        f -= axis(p.axes[1]);
        r += axis(p.axes[0]);
        oy += axis(p.axes[2]);
        op += axis(p.axes[3]);
        if (p.buttons[4]?.pressed) u -= 1;
        if (p.buttons[5]?.pressed) u += 1;
        z += (p.buttons[7]?.value ?? (p.buttons[7]?.pressed ? 1 : 0)) - (p.buttons[6]?.value ?? (p.buttons[6]?.pressed ? 1 : 0));
      }
      const c = (v: number): number => Math.max(-1, Math.min(1, v));
      if (f !== 0 || r !== 0 || u !== 0) hd.move(c(f), c(r), c(u), dt);
      if (oy !== 0 || op !== 0) hd.orbit(oy * padOrbit * dt, op * padOrbit * dt);
      if (Math.abs(z) > dead) hd.zoom(Math.pow(zoomRate, z * dt));
    },
    held: () => [...held],
  };
}

/** Props of {@link PhotoSurface}. */
export interface PhotoSurfaceProps {
  handlers: PhotoHandlers;
  /** The surface's accessible name (a translated string: what it does). */
  label: string;
  options?: PhotoInputOptions;
  /** Read the pads; default `navigator.getGamepads()`. */
  pads?: () => readonly (PadLike | null)[];
  /** The game's frames (`tick(dt)` once per drawn frame; returns the unsubscribe): held keys and the pads move on
   *  them, so a game's deterministic frames drive photo mode too. Default the page's own animation frames. */
  frames?: (tick: (dt: number) => void) => () => void;
}

/** The camera surface under photo mode's docked panel: drag, wheel, pinch, keys and pads move the game's camera. */
export function PhotoSurface(p: PhotoSurfaceProps): El {
  const ref = useRef<HTMLDivElement | null>(null);
  const props = useRef(p);
  props.current = p;
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const doc = el.ownerDocument;
    const win = doc.defaultView!;
    const input = createPhotoInput(
      {
        move: (...a) => props.current.handlers.move(...a),
        orbit: (...a) => props.current.handlers.orbit(...a),
        zoom: (f) => props.current.handlers.zoom(f),
      },
      p.options,
    );
    const typing = (): boolean => {
      const a = doc.activeElement as HTMLElement | null;
      return !!a && (a.tagName === "INPUT" && (a as HTMLInputElement).type === "text");
    };
    const onKey = (e: KeyboardEvent): void => {
      if (typing() || e.repeat) return;
      if (input.key(e.code, e.type === "keydown")) e.preventDefault();
    };
    const onBlur = (): void => {
      for (const k of input.held()) input.key(k, false);
    };
    const onDown = (e: PointerEvent): void => {
      el.setPointerCapture?.(e.pointerId);
      input.pointerDown(e.pointerId, e.clientX, e.clientY);
    };
    const onMove = (e: PointerEvent): void => input.pointerMove(e.pointerId, e.clientX, e.clientY);
    const onUp = (e: PointerEvent): void => input.pointerUp(e.pointerId);
    const onWheel = (e: WheelEvent): void => {
      e.preventDefault();
      input.wheel(e.deltaY);
    };
    doc.addEventListener("keydown", onKey);
    doc.addEventListener("keyup", onKey);
    win.addEventListener("blur", onBlur);
    el.addEventListener("pointerdown", onDown);
    el.addEventListener("pointermove", onMove);
    el.addEventListener("pointerup", onUp);
    el.addEventListener("pointercancel", onUp);
    el.addEventListener("wheel", onWheel, { passive: false });
    const readPads = props.current.pads ?? (() => (typeof navigator !== "undefined" && navigator.getGamepads ? navigator.getGamepads() : []));
    const step = (dt: number): void => input.frame(Math.min(0.1, Math.max(0, dt)), readPads() as Parameters<PhotoInput["frame"]>[1]);
    let raf = 0;
    let last = win.performance.now();
    const tick = (): void => {
      const now = win.performance.now();
      step((now - last) / 1000);
      last = now;
      raf = win.requestAnimationFrame(tick);
    };
    const off = p.frames ? p.frames(step) : null;
    if (!off) raf = win.requestAnimationFrame(tick);
    return () => {
      doc.removeEventListener("keydown", onKey);
      doc.removeEventListener("keyup", onKey);
      win.removeEventListener("blur", onBlur);
      el.removeEventListener("pointerdown", onDown);
      el.removeEventListener("pointermove", onMove);
      el.removeEventListener("pointerup", onUp);
      el.removeEventListener("pointercancel", onUp);
      el.removeEventListener("wheel", onWheel);
      if (raf) win.cancelAnimationFrame(raf);
      off?.();
    };
  }, []);
  return h("div", { ref, class: "rpg-photo-surface", role: "application", "aria-label": p.label, "data-photo-surface": "", "data-pad-stick": "off" });
}
